/* ═══════════════════════════════════════════════════════════════
   pk-overlays — nav scroll states + top-drop overlay controller
   (mega-menu / search / cart), predictive search, AJAX cart.
   Overlays are mutually exclusive; closing plays the reverse
   animation (~340ms) before hiding — mirrors the design prototype.
   ═══════════════════════════════════════════════════════════════ */
(() => {
  'use strict';

  const CLOSE_MS = 340;
  const nav = document.querySelector('[data-pk-nav]');
  const overlays = {};
  document.querySelectorAll('[data-pk-overlay]').forEach((el) => {
    overlays[el.dataset.pkOverlay] = el;
  });

  let openKey = null;
  let closingTimer = null;
  let lastTrigger = null; // control that opened the current overlay, for focus return

  /* ── nav scroll state ── */
  const heroPage = document.body.classList.contains('pk-has-hero');
  const syncNav = () => {
    if (!nav) return;
    const scrolled = window.scrollY > 24;
    nav.classList.toggle('is-scrolled', scrolled);
    // hero (transparent) only at top of hero pages with no overlay open
    nav.classList.toggle('is-hero', heroPage && !scrolled && !openKey);
  };
  window.addEventListener('scroll', syncNav, { passive: true });

  /* ── overlay controller ── */
  const syncToggles = () => {
    document.querySelectorAll('[data-pk-toggle]').forEach((btn) => {
      const on = btn.dataset.pkToggle === openKey;
      btn.setAttribute('aria-expanded', on ? 'true' : 'false');
      if (btn.classList.contains('pk-iconswap')) btn.dataset.x = on ? 'true' : 'false';
    });
  };

  const open = (key, trigger) => {
    const el = overlays[key];
    if (!el) return;
    if (trigger) lastTrigger = trigger;
    if (closingTimer) {
      clearTimeout(closingTimer);
      closingTimer = null;
      Object.values(overlays).forEach((o) => {
        o.classList.remove('is-closing');
        o.hidden = true;
      });
    }
    if (openKey && openKey !== key) overlays[openKey].hidden = true;
    openKey = key;
    el.classList.remove('is-closing');
    el.hidden = false;
    // page keeps scrolling beneath the panel (desktop + mobile), per design
    document.body.classList.add('pk-overlay-open');
    syncToggles();
    syncNav();
    if (key === 'search') {
      setTimeout(() => el.querySelector('[data-pk-search-input]')?.focus(), 60);
    }
    el.querySelector('.pk-overlay__panel')?.setAttribute('tabindex', '-1');
  };

  const close = () => {
    if (!openKey) return;
    const el = overlays[openKey];
    openKey = null;
    el.classList.add('is-closing');
    document.body.classList.remove('pk-overlay-open');
    // Return focus to the control that opened the overlay (keyboard/SR users
    // keep their place instead of being dropped to <body>). The toggles have no
    // custom focus CSS, so the browser's :focus-visible heuristic shows a ring
    // only for keyboard closes — a mouse scrim-close returns focus silently.
    const returnTo = lastTrigger;
    lastTrigger = null;
    if (returnTo && document.contains(returnTo) && typeof returnTo.focus === 'function') {
      returnTo.focus();
    } else if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    syncToggles();
    syncNav();
    closingTimer = setTimeout(() => {
      closingTimer = null;
      el.classList.remove('is-closing');
      el.hidden = true;
    }, CLOSE_MS);
  };

  const toggle = (key, trigger) => (openKey === key ? close() : open(key, trigger));

  document.addEventListener('click', (e) => {
    const toggleBtn = e.target.closest('[data-pk-toggle]');
    if (toggleBtn) {
      e.preventDefault();
      toggle(toggleBtn.dataset.pkToggle, toggleBtn);
      return;
    }
    if (e.target.closest('[data-pk-close]')) {
      close();
      return;
    }
    // fallback: a click that lands on an overlay root (outside its panel)
    // always closes — the scrim should catch this, but never trap the user
    if (e.target.classList.contains('pk-overlay')) {
      close();
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });

  /* ── predictive search — document-delegated so it survives section
     re-renders (theme editor / hot reload replace the header DOM) ── */
  let searchDebounce = null;
  let searchController = null;
  const resultsEl = () => document.querySelector('[data-pk-search-results]');

  document.addEventListener('input', (e) => {
    const input = e.target.closest('[data-pk-search-input]');
    if (!input) return;
    const q = input.value.trim();
    clearTimeout(searchDebounce);
    if (!q) {
      const el = resultsEl();
      if (el) el.innerHTML = '';
      return;
    }
    searchDebounce = setTimeout(async () => {
      searchController?.abort();
      searchController = new AbortController();
      try {
        const url = `${window.routes.predictive_search_url}?q=${encodeURIComponent(q)}&resources[type]=product,collection&resources[limit]=6&section_id=pk-search-results`;
        const res = await fetch(url, { signal: searchController.signal });
        if (!res.ok) return;
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const el = resultsEl();
        if (el) el.innerHTML = doc.querySelector('.shopify-section')?.innerHTML || '';
      } catch (err) {
        if (err.name !== 'AbortError') {
          const el = resultsEl();
          if (el) el.innerHTML = '';
        }
      }
    }, 220);
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' || !e.target.closest?.('[data-pk-search-input]')) return;
    const first = resultsEl()?.querySelector('.pk-searchrow');
    if (first) {
      e.preventDefault();
      window.location.href = first.href;
    }
  });

  /* ── AJAX cart ── */
  const refreshCart = (sectionHTML) => {
    const inner = document.querySelector('[data-pk-cart-inner]');
    if (inner && sectionHTML) {
      const doc = new DOMParser().parseFromString(sectionHTML, 'text/html');
      const fresh = doc.querySelector('[data-pk-cart-inner]');
      if (fresh) inner.innerHTML = fresh.innerHTML;
      inner.classList.remove('is-busy');
    }
  };

  const updateBadge = (count) => {
    document.querySelectorAll('[data-pk-cart-count]').forEach((b) => {
      b.textContent = count;
      b.hidden = count === 0;
    });
  };

  const changeLine = async (line, quantity) => {
    document.querySelector('[data-pk-cart-inner]')?.classList.add('is-busy');
    try {
      const res = await fetch(window.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: 'pk-cart-panel' }),
      });
      const data = await res.json();
      updateBadge(data.item_count ?? 0);
      refreshCart(data.sections?.['pk-cart-panel']);
    } catch (err) {
      document.querySelector('[data-pk-cart-inner]')?.classList.remove('is-busy');
    }
  };

  document.addEventListener('click', (e) => {
    const lineEl = e.target.closest('[data-pk-line]');
    if (!lineEl) return;
    const line = parseInt(lineEl.dataset.pkLine, 10);
    const qtyEl = lineEl.querySelector('[data-pk-qty-value]');
    const qty = qtyEl ? parseInt(qtyEl.textContent, 10) : 1;
    const action = e.target.closest('[data-pk-qty]')?.dataset.pkQty;
    if (action === 'inc') changeLine(line, qty + 1);
    else if (action === 'dec') changeLine(line, Math.max(1, qty - 1));
    else if (e.target.closest('[data-pk-remove]')) changeLine(line, 0);
  });

  /* public API — PDP add-to-cart opens the panel with fresh contents */
  window.PKCart = {
    async add(formData) {
      formData.append('sections', 'pk-cart-panel');
      const res = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      const data = await res.json();
      if (data.status) throw new Error(data.description || 'Could not add to cart');
      const cartRes = await fetch('/cart.js');
      const cartData = await cartRes.json();
      updateBadge(cartData.item_count);
      refreshCart(data.sections?.['pk-cart-panel']);
      open('cart');
      return data;
    },
    open: () => open('cart'),
    close,
  };

  syncNav();
  syncToggles();

  /* ── IntersectionObserver fallback for scroll reveals ── */
  if (!CSS.supports('animation-timeline: view()')) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('pk-inview');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -12% 0px' }
    );
    document.querySelectorAll('.pk-reveal, .pk-reveal-slow, .pk-linegrow').forEach((el) => io.observe(el));
  }
})();
